import { ReactNode, useEffect, useRef, useState } from 'react';
import { FieldProvenanceType } from '../../schemas/report.schemas';
import './provenanceDetails.css';

// The full grounding payload for one field, shown when a highlight on the source document is
// clicked. Everything the backend sends is surfaced here — the readable fields for judging whether
// an extraction is trustworthy, and the raw JSON for pasting into a bug report. Only employees with
// access to the current view reach this: it renders inside Source view, which is gated by
// `isSourceViewAvailable`, and restricted reviews keep it hidden.

type ProvenanceDetailsPropsType = {
    entry: FieldProvenanceType;
};

// How long the Copy button stays in its confirmed state before reverting.
const COPY_FEEDBACK_MS = 1500;

const COPY_IDLE_LABEL = 'Copy JSON';

type DetailRowPropsType = {
    label: string;
    value: ReactNode;
};

// A row is omitted rather than shown empty: most of these fields are nullable and a screen of
// "Printed row label: —" says less than not listing it at all.
function DetailRow({ label, value }: DetailRowPropsType) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    return (
        <>
            <dt className="provenance-details__label">{label}</dt>
            <dd className="provenance-details__value">{value}</dd>
        </>
    );
}

function ProvenanceDetails({ entry }: ProvenanceDetailsPropsType) {
    const [copyLabel, setCopyLabel] = useState(COPY_IDLE_LABEL);
    const revertTimeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => clearTimeout(revertTimeout.current);
    }, []);

    const json = JSON.stringify(entry, null, 2);

    const flashLabel = (label: string) => {
        setCopyLabel(label);
        clearTimeout(revertTimeout.current);
        revertTimeout.current = setTimeout(() => setCopyLabel(COPY_IDLE_LABEL), COPY_FEEDBACK_MS);
    };

    const copyJson = () => {
        // The clipboard API needs a secure context, so it is missing over plain http.
        if (!navigator.clipboard) {
            flashLabel('Copy failed');
            return;
        }
        navigator.clipboard.writeText(json).then(
            () => flashLabel('Copied'),
            () => flashLabel('Copy failed'),
        );
    };

    return (
        <div className="provenance-details">
            <div className="provenance-details__header">
                <span className="provenance-details__page">Page {entry.page}</span>
                {/* Neutral on purpose: the GroundingStatus value set is not pinned down yet, so
                    there is nothing to colour good or bad against. */}
                <span className="provenance-details__status">{entry.status}</span>
                {entry.confidence !== null && entry.confidence !== undefined && (
                    // Printed raw rather than as a percentage — whether the backend scales it 0–1
                    // or 0–100 is not established, and guessing would misreport it.
                    <span className="provenance-details__confidence">confidence {entry.confidence}</span>
                )}
            </div>

            <dl className="provenance-details__grid">
                <DetailRow label="Printed value" value={entry.printedValue} />
                <DetailRow label="Printed row label" value={entry.printedRowLabel} />
                <DetailRow label="Printed column label" value={entry.printedColumnLabel} />
                <DetailRow label="Descriptor id" value={entry.descriptorId} />
                <DetailRow label="Value regions" value={entry.valueRegions.length} />
                <DetailRow label="Label regions" value={entry.labelRegions.length} />
            </dl>

            <div className="provenance-details__json-header">
                <span>Raw</span>
                <button type="button" className="provenance-details__copy" onClick={copyJson}>
                    {copyLabel}
                </button>
            </div>
            <pre className="provenance-details__json">{json}</pre>
        </div>
    );
}

export default ProvenanceDetails;
